import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Label } from '../../../components/common/Label';
import { useToast } from '../../../components/common/Toast';
import { PageHeader } from '../../../components/common/PageHeader';
import { Save, Building2, MapPin, Target, Settings, User } from 'lucide-react';

interface AccountFormData {
  // Basic Information
  name: string;
  domain: string;
  status: string;
  accountType: string;
  partnerType: string;
  partnerTerritory: string;
  partnerMarketFocus: string;
  partnerPortfolio: string;
  partnerDeliveryCapacity: string;
  partnerDealModel: string;
  
  // Industry & Location
  industryVertical: string;
  subVertical: string;
  region: string;
  plantType: string;
  
  // ABM Configuration
  abmTier: number;
  maturityBand: string;
  maturityConfidence: string;
  
  // Technical Profile
  primaryPainAxis: string;
  painDescription: string;
  additionalNotes: string;
  dataReadinessLevel: string;
  automationStage: string;
  architectureTags: string;
  
  // Assignment
  assignedMdaId: string;
}

const AddEditAccountPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [formData, setFormData] = useState<AccountFormData>({
    name: '',
    domain: '',
    status: 'ACTIVE',
    accountType: 'BUYER',
    partnerType: '',
    partnerTerritory: '',
    partnerMarketFocus: '',
    partnerPortfolio: '',
    partnerDeliveryCapacity: '',
    partnerDealModel: '',
    industryVertical: '',
    subVertical: '',
    region: '',
    plantType: '',
    abmTier: 1,
    maturityBand: '',
    maturityConfidence: '',
    primaryPainAxis: '',
    painDescription: '',
    additionalNotes: '',
    dataReadinessLevel: '',
    automationStage: '',
    architectureTags: '',
    assignedMdaId: ''
  });

  const isEditMode = !!id;

  // Fetch users for assignment dropdown
  useEffect(() => {
    fetchUsers();
    if (isEditMode) {
      fetchAccount();
    }
  }, [id]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      console.log('🔑 fetchUsers - Token from localStorage:', token ? 'EXISTS' : 'NULL', token?.substring(0, 20));
      
      const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAccount = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      console.log('🔑 fetchAccount - Token from localStorage:', token ? 'EXISTS' : 'NULL', token?.substring(0, 20));
      console.log('Fetching account:', id);
      
      const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/accounts/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        throw new Error('Failed to fetch account');
      }

      const account = await response.json();
      console.log('Account data received:', account);
      
      setFormData({
        name: account.name || '',
        domain: account.domain || '',
        status: account.status || 'ACTIVE',
        accountType: (account.accountType || 'BUYER').toUpperCase() === 'PARTNER' ? 'PARTNER' : 'BUYER',
        partnerType: account.partnerType || '',
        partnerTerritory: account.partnerTerritory || '',
        partnerMarketFocus: account.partnerMarketFocus || '',
        partnerPortfolio: account.partnerPortfolio || '',
        partnerDeliveryCapacity: account.partnerDeliveryCapacity || '',
        partnerDealModel: account.partnerDealModel || '',
        industryVertical: account.industryVertical || '',
        subVertical: account.subVertical || '',
        region: account.region || '',
        plantType: account.plantType || '',
        abmTier: account.abmTier || 1,
        maturityBand: account.maturityBand || '',
        maturityConfidence: account.maturityConfidence || '',
        primaryPainAxis: account.primaryPainAxis || '',
        painDescription: account.painDescription || '',
        additionalNotes: account.additionalNotes || '',
        dataReadinessLevel: account.dataReadinessLevel || '',
        automationStage: account.automationStage || '',
        architectureTags: account.architectureTags ? account.architectureTags.join(', ') : '',
        assignedMdaId: account.assignedMdaId || ''
      });
      
      console.log('Form data set successfully');
    } catch (error) {
      console.error('Error fetching account:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to load account'
      });
      navigate('/accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      addToast({
        type: 'error',
        title: 'Validation Error',
        description: 'Account name is required'
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const apiBase = import.meta.env.VITE_API_URL ?? '';
      const url = isEditMode 
        ? `${apiBase}/api/accounts/${id}`
        : `${apiBase}/api/accounts`;
      
      const method = isEditMode ? 'PUT' : 'POST';

      // Process architecture tags
      const architectureTagsArray = formData.architectureTags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const payload = {
        ...formData,
        architectureTags: architectureTagsArray,
        abmTier: parseInt(formData.abmTier.toString()),
        maturityBand: formData.maturityBand || null,
        primaryPainAxis: formData.primaryPainAxis || null,
        assignedMdaId: formData.assignedMdaId || null,
        accountType: formData.accountType === 'PARTNER' ? 'PARTNER' : 'BUYER',
        partnerType: formData.accountType === 'PARTNER' ? (formData.partnerType || null) : null
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Failed to ${isEditMode ? 'update' : 'create'} account`);
      }

      addToast({
        type: 'success',
        title: 'Success',
        description: `Account ${isEditMode ? 'updated' : 'created'} successfully`
      });
      navigate('/accounts');
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} account:`, error);
      addToast({
        type: 'error',
        title: 'Error',
        description: `Failed to ${isEditMode ? 'update' : 'create'} account`
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof AccountFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <PageHeader
        onBack={() => navigate('/accounts')}
        title={isEditMode ? 'Edit Account' : 'Add New Account'}
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <CardTitle>Basic Information</CardTitle>
            </div>
            <CardDescription>
              Essential account details and status
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Account Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter account name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="domain">Domain</Label>
                <Input
                  id="domain"
                  type="text"
                  value={formData.domain}
                  onChange={(e) => handleChange('domain', e.target.value)}
                  placeholder="example.com"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="accountType">Account Type</Label>
                <select
                  id="accountType"
                  value={formData.accountType}
                  onChange={(e) => handleChange('accountType', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="BUYER">Buyer (end customer → MQL)</option>
                  <option value="PARTNER">Partner (channel → PQL)</option>
                </select>
                <p className="text-sm text-muted-foreground">
                  Determines the 14-step journey track and qualification model.
                </p>
              </div>

              {formData.accountType === 'PARTNER' && (
                <div className="space-y-2">
                  <Label htmlFor="partnerType">Partner Type</Label>
                  <select
                    id="partnerType"
                    value={formData.partnerType}
                    onChange={(e) => handleChange('partnerType', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select Partner Type</option>
                    <option value="RESELLER">Reseller</option>
                    <option value="SYSTEM_INTEGRATOR">System Integrator (SI)</option>
                    <option value="DISTRIBUTOR">Distributor</option>
                    <option value="VAR">Value-Added Reseller (VAR)</option>
                    <option value="CONSULTANT">Consultant / Advisory</option>
                    <option value="OEM">Technology / OEM Partner</option>
                  </select>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="ACTIVE">Active</option>
                <option value="MQL_READY">MQL Ready</option>
                <option value="MQL_SUBMITTED">MQL Submitted</option>
                <option value="FROZEN">Frozen</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="additionalNotes">Additional Notes</Label>
              <textarea
                id="additionalNotes"
                rows={3}
                value={formData.additionalNotes}
                onChange={(e) => handleChange('additionalNotes', e.target.value)}
                placeholder="Any other context about this account (buyer or partner)"
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
          </CardContent>
        </Card>

        {/* Partner Profile — only for PARTNER accounts */}
        {formData.accountType === 'PARTNER' && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                <CardTitle>Partner Profile</CardTitle>
              </div>
              <CardDescription>
                Channel-partner details — used for the partner (PQL) journey instead of the buyer MES profile
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="partnerTerritory">Territory Served</Label>
                  <Input
                    id="partnerTerritory"
                    type="text"
                    value={formData.partnerTerritory}
                    onChange={(e) => handleChange('partnerTerritory', e.target.value)}
                    placeholder="e.g. South India, APAC, EMEA"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="partnerMarketFocus">Market / Vertical Focus</Label>
                  <Input
                    id="partnerMarketFocus"
                    type="text"
                    value={formData.partnerMarketFocus}
                    onChange={(e) => handleChange('partnerMarketFocus', e.target.value)}
                    placeholder="e.g. Discrete manufacturing, Automotive, F&B"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="partnerPortfolio">Current Portfolio (what they sell today)</Label>
                <Input
                  id="partnerPortfolio"
                  type="text"
                  value={formData.partnerPortfolio}
                  onChange={(e) => handleChange('partnerPortfolio', e.target.value)}
                  placeholder="e.g. SCADA/PLC integration, ERP resale, IIoT sensors"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="partnerDeliveryCapacity">Delivery / Enablement Capacity</Label>
                  <select
                    id="partnerDeliveryCapacity"
                    value={formData.partnerDeliveryCapacity}
                    onChange={(e) => handleChange('partnerDeliveryCapacity', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select capacity</option>
                    <option value="none">None — needs full enablement</option>
                    <option value="basic">Basic — some capacity</option>
                    <option value="strong">Strong — ready to deliver</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="partnerDealModel">Deal / Commercial Model</Label>
                  <select
                    id="partnerDealModel"
                    value={formData.partnerDealModel}
                    onChange={(e) => handleChange('partnerDealModel', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select model</option>
                    <option value="reseller">Reseller (margin on resale)</option>
                    <option value="referral">Referral (commission)</option>
                    <option value="distributor">Distributor (volume)</option>
                    <option value="si_delivery">SI / delivery-led</option>
                    <option value="coSell">Co-sell</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Industry & Location */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              <CardTitle>Industry & Location</CardTitle>
            </div>
            <CardDescription>
              Industry classification and geographic information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="industryVertical">Industry Vertical</Label>
                <Input
                  id="industryVertical"
                  type="text"
                  value={formData.industryVertical}
                  onChange={(e) => handleChange('industryVertical', e.target.value)}
                  placeholder="e.g., Semiconductor"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subVertical">Sub-Vertical</Label>
                <Input
                  id="subVertical"
                  type="text"
                  value={formData.subVertical}
                  onChange={(e) => handleChange('subVertical', e.target.value)}
                  placeholder="e.g., OSAT, IDM"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="region">Region</Label>
                <Input
                  id="region"
                  type="text"
                  value={formData.region}
                  onChange={(e) => handleChange('region', e.target.value)}
                  placeholder="e.g., North America, APAC"
                />
              </div>

              {formData.accountType !== 'PARTNER' && (
                <div className="space-y-2">
                  <Label htmlFor="plantType">Plant Type</Label>
                  <Input
                    id="plantType"
                    type="text"
                    value={formData.plantType}
                    onChange={(e) => handleChange('plantType', e.target.value)}
                    placeholder="e.g., Fab, Assembly"
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ABM Configuration */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              <CardTitle>ABM Configuration</CardTitle>
            </div>
            <CardDescription>
              Account-based marketing tier and maturity settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="abmTier">ABM Tier</Label>
                <select
                  id="abmTier"
                  value={formData.abmTier.toString()}
                  onChange={(e) => handleChange('abmTier', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="1">Tier 1 (Strategic)</option>
                  <option value="2">Tier 2 (Target)</option>
                  <option value="3">Tier 3 (Engage)</option>
                </select>
              </div>

              {formData.accountType !== 'PARTNER' && (
                <div className="space-y-2">
                  <Label htmlFor="maturityBand">Maturity Band</Label>
                  <select
                    id="maturityBand"
                    value={formData.maturityBand}
                    onChange={(e) => handleChange('maturityBand', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select Band</option>
                    <option value="L1">L1 (Beginner)</option>
                    <option value="L2">L2 (Developing)</option>
                    <option value="L3">L3 (Defined)</option>
                    <option value="L4">L4 (Managed)</option>
                    <option value="L5">L5 (Optimized)</option>
                  </select>
                </div>
              )}

              {formData.accountType !== 'PARTNER' && (
                <div className="space-y-2">
                  <Label htmlFor="maturityConfidence">Maturity Confidence</Label>
                  <Input
                    id="maturityConfidence"
                    type="text"
                    value={formData.maturityConfidence}
                    onChange={(e) => handleChange('maturityConfidence', e.target.value)}
                    placeholder="e.g., High, Medium, Low"
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Technical Profile — buyer (MES) only */}
        {formData.accountType !== 'PARTNER' && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              <CardTitle>Technical Profile</CardTitle>
            </div>
            <CardDescription>
              Technology stack and digital maturity details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="primaryPainAxis">Primary Pain Axis</Label>
                <select
                  id="primaryPainAxis"
                  value={formData.primaryPainAxis}
                  onChange={(e) => handleChange('primaryPainAxis', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Select Pain Axis</option>
                  <option value="VISIBILITY">Visibility</option>
                  <option value="EXECUTION">Execution</option>
                  <option value="DATA">Data</option>
                  <option value="QUALITY">Quality</option>
                  <option value="INTEGRATION">Integration</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dataReadinessLevel">Data Readiness Level</Label>
                <Input
                  id="dataReadinessLevel"
                  type="text"
                  value={formData.dataReadinessLevel}
                  onChange={(e) => handleChange('dataReadinessLevel', e.target.value)}
                  placeholder="e.g., Basic, Intermediate, Advanced"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="painDescription">Pain Description</Label>
              <textarea
                id="painDescription"
                rows={3}
                value={formData.painDescription}
                onChange={(e) => handleChange('painDescription', e.target.value)}
                placeholder="Describe the operational pain in detail — what's breaking, where, and its impact"
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="automationStage">Automation Stage</Label>
              <Input
                id="automationStage"
                type="text"
                value={formData.automationStage}
                onChange={(e) => handleChange('automationStage', e.target.value)}
                placeholder="e.g., Manual, Semi-Automated, Fully Automated"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="architectureTags">Architecture Tags</Label>
              <Input
                id="architectureTags"
                type="text"
                value={formData.architectureTags}
                onChange={(e) => handleChange('architectureTags', e.target.value)}
                placeholder="Enter tags separated by commas (e.g., MES, IoT, Cloud)"
              />
              <p className="text-sm text-muted-foreground">
                Separate multiple tags with commas
              </p>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Assignment */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              <CardTitle>Assignment</CardTitle>
            </div>
            <CardDescription>
              Assign account owner and team
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="assignedMdaId">Assigned MDA</Label>
              <select
                id="assignedMdaId"
                value={formData.assignedMdaId}
                onChange={(e) => handleChange('assignedMdaId', e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">Select MDA</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/accounts')}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isEditMode ? 'Update Account' : 'Create Account'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddEditAccountPage;
